import { createElement } from "react";
import { Trans } from "react-i18next";
import { deleteHttp, post } from "@common/api/apiShared";
import { useDotLegalMutation, useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { getSettings } from "@common/settingsProvider";
import { useUrlProvider } from "../../../useUrlProvider";
import { RiskObservationPublicLinkViewModel } from "./RiskObservationPublicLinkDialog.types";

const publicLinkUrl = "/observations/public-link";

export function useRiskObservationPublicLinkDialogDataMapping() {
    const { translateString } = useTranslation();
    const snackbar = useDotLegalSnackbar();
    const queryClient = useDotLegalQueryClient();
    const urlProvider = useUrlProvider();

    const { data, isLoading } = useDotLegalQuery<RiskObservationPublicLinkViewModel>(publicLinkUrl);

    function updateCache(externalId: string | undefined, isActive: boolean) {
        queryClient.setQueryData<RiskObservationPublicLinkViewModel>(publicLinkUrl, { externalId, isActive });
    }

    const enableMutation = useDotLegalMutation(() => post<RiskObservationPublicLinkViewModel>(publicLinkUrl, {}), {
        onSuccess: (response) => {
            if (response.success()) {
                updateCache(response.value().externalId, response.value().isActive);
                snackbar.show(translateString("reportObservationLinkEnabled"));
            }
        },
    });

    const disableMutation = useDotLegalMutation(() => deleteHttp(publicLinkUrl), {
        onSuccess: (response) => {
            if (response.success()) {
                updateCache(data?.externalId, false);
                snackbar.show(translateString("reportObservationLinkDisabled"));
            }
        },
    });

    const isActive = data?.isActive ?? false;
    const shareUrl = isActive && data?.externalId ? getSettings().mainSiteBaseUrl + urlProvider.getPublicReportObservationUrl(data.externalId) : undefined;

    function copyShareLink() {
        if (shareUrl) {
            navigator.clipboard.writeText(shareUrl);
            snackbar.show(createElement(Trans, { i18nKey: "linkWasCopied", values: { link: shareUrl } }));
        }
    }

    return {
        isLoading,
        isActive,
        shareUrl,
        isToggling: enableMutation.isLoading || disableMutation.isLoading,
        makeActive: () => enableMutation.mutate(),
        makeInactive: () => disableMutation.mutate(),
        copyShareLink,
    };
}
